import { type FunctionComponent, useState } from "react";
import { type HighlightArea } from "@react-pdf-viewer/highlight";
import { parseISO } from "date-fns";
import { VerifyRow } from "@/components/feature/deadline_guardian/verification_view/VerifyRow";
import { courtDataUpdatedMock } from "@/views/home/deadline_guardian/mocks/courtEventsMockData";

// Define the types for the props
export interface VerifyTableProps {
  courtData: Array<{
    id: string;
    date: string;
    party: string;
    summary: Array<{
      text: string;
      cites: Array<{
        text: string;
        page: number;
        highlightText: string;
        highlightAreas: HighlightArea[];
      }>;
    }>;
    verificationScore: number;
    futureDate: boolean;
    missingDay: boolean;
  }>;
  handleHighlight: (highlightAreas: HighlightArea[], page: number) => void;
}

// Filter the events based on futureDate and missingDay criteria
const filteredCourtData = courtDataUpdatedMock.filter(event => !event.futureDate || event.missingDay);

export const VerifyTable: FunctionComponent<VerifyTableProps> = ({
  courtData = filteredCourtData,
  handleHighlight,
}) => {
  const [, setSelectedDates] = useState<Record<string, Date | null>>(() =>
    courtData.reduce(
      (acc, cur) => {
        acc[cur.id] = cur.date ? parseISO(cur.date) : null;
        return acc;
      },
      {} as Record<string, Date | null>,
    ),
  );

  const [, setParties] = useState<Record<string, string>>(() =>
    courtData.reduce(
      (acc, cur) => {
        acc[cur.id] = cur.party;
        return acc;
      },
      {} as Record<string, string>,
    ),
  );

  const handleDateChange = (date: Date | null, caseId: string) => {
    setSelectedDates(prev => ({ ...prev, [caseId]: date }));
  };

  const handlePartyChange = (event: React.ChangeEvent<HTMLInputElement>, caseId: string) => {
    setParties(prev => ({ ...prev, [caseId]: event.target.value }));
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <p className="mt-2 text-sm text-gray-700">
            A list of all court cases including their date, party, summary, verification score, and actions.
          </p>
        </div>
      </div>
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Date/Party
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Summary
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Verification
                    </th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 text-right sm:pr-6">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {filteredCourtData.map(caseItem => (
                    <VerifyRow
                      key={caseItem.id}
                      caseItem={caseItem}
                      handleDateChange={handleDateChange}
                      handlePartyChange={handlePartyChange}
                      handleHighlight={handleHighlight}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
